"use client";

import Link from "next/link";
import { useMemo, useState } from "react";

type Summary = { slug: string; name: string; ecoRange: string; count: number; mainLine: string; firstMove: string };

const FIRST_MOVES = ["All", "e4", "d4", "c4", "Nf3", "Other"];

/** Client-side filter over the opening families. */
export function OpeningSearch({ families, featured }: { families: Summary[]; featured: string[] }) {
  const [q, setQ] = useState("");
  const [first, setFirst] = useState("All");
  const [sort, setSort] = useState<"popular" | "az">("popular");

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    let list = families.filter((f) => {
      const hay = `${f.name} ${f.ecoRange} ${f.mainLine}`.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
      if (needle && !hay.includes(needle)) return false;
      if (first === "All") return true;
      if (first === "Other") return !["e4", "d4", "c4", "Nf3"].includes(f.firstMove);
      return f.firstMove === first;
    });
    list = [...list].sort((a, b) => (sort === "az" ? a.name.localeCompare(b.name) : b.count - a.count));
    return list;
  }, [families, q, first, sort]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor="opening-q">
          Search openings
        </label>
        <input
          id="opening-q"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, ECO code or moves, e.g. Najdorf, B90, e4 c5"
          className="min-w-0 flex-1 rounded-lg border border-border bg-surface px-3 py-2"
        />
        <select value={sort} onChange={(e) => setSort(e.target.value as "popular" | "az")} className="rounded-lg border border-border bg-surface px-2 py-2 text-sm" aria-label="Sort">
          <option value="popular">Most lines first</option>
          <option value="az">A to Z</option>
        </select>
      </div>
      <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="First move">
        {FIRST_MOVES.map((m) => (
          <button
            key={m}
            onClick={() => setFirst(m)}
            aria-pressed={first === m}
            className={`rounded-full border px-3 py-1 text-sm ${first === m ? "border-accent bg-accent-soft" : "border-border"}`}
          >
            {m === "All" || m === "Other" ? m : `1.${m}`}
          </button>
        ))}
      </div>
      <p className="mt-3 text-sm text-muted" aria-live="polite">
        {shown.length} {shown.length === 1 ? "family" : "families"}
      </p>
      <ul className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((f) => (
          <li key={f.slug}>
            <Link href={`/openings/${f.slug}`} className="block h-full rounded-xl border border-border bg-surface p-3 hover:border-accent">
              <span className="flex items-baseline justify-between gap-2">
                <span className="font-medium">{f.name}</span>
                {featured.includes(f.slug) && <span className="text-xs text-accent">Guide</span>}
              </span>
              <span className="mt-0.5 block font-mono text-xs text-muted">{f.mainLine}</span>
              <span className="mt-1 block text-xs text-muted">
                {f.ecoRange} · {f.count} {f.count === 1 ? "line" : "lines"}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
